import { DEFAULT_CURRENCY, formatMoney } from "@vouch/shared";
import { Brain, Sparkles } from "lucide-react";
import { Button, Card, Spinner } from "../components/ui";
import { useAiAvailable, useInvestSuggestions } from "../lib/hooks";

const RISK_COLOR: Record<string, string> = {
  low: "text-green-600 bg-green-50",
  medium: "text-amber-600 bg-amber-50",
  high: "text-red-600 bg-red-50",
};

export function InsightsPage(): JSX.Element {
  const { data: available, isLoading } = useAiAvailable();
  const suggest = useInvestSuggestions();

  if (isLoading) return <Spinner />;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center gap-2">
        <Brain className="text-brand-500" />
        <h1 className="text-2xl font-bold text-brand-500">AI Insights</h1>
      </div>

      {!available ? (
        <Card>
          <p className="text-sm text-slate-500">
            AI insights aren't configured for this environment yet.
          </p>
        </Card>
      ) : (
        <>
          <Card className="bg-brand-500 text-white">
            <p className="text-sm opacity-80">
              Get a finance-aware view of how much you could safely invest, based on your real
              balances, income, expenses and outstanding loans.
            </p>
            <Button
              variant="secondary"
              className="mt-3"
              onClick={() => suggest.mutate()}
              disabled={suggest.isPending}
            >
              <Sparkles size={16} /> {suggest.isPending ? "Thinking…" : "Get suggestions"}
            </Button>
          </Card>

          {suggest.isError && (
            <Card>
              <p className="text-sm text-red-600">
                Couldn't get suggestions right now. Please try again later.
              </p>
            </Card>
          )}

          {suggest.data && (
            <>
              <Card>
                <p className="text-sm text-slate-500">Safe to invest now</p>
                <p className="mt-1 text-2xl font-bold text-brand-500">
                  {formatMoney(suggest.data.advice.safeToInvestMinor, DEFAULT_CURRENCY)}
                </p>
                {suggest.data.advice.notes && (
                  <p className="mt-2 text-sm text-slate-600">{suggest.data.advice.notes}</p>
                )}
              </Card>

              <div className="grid gap-3">
                {suggest.data.advice.suggestions.map((s, i) => (
                  <Card key={i}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-700">{s.instrument}</p>
                        <p className="mt-1 text-sm text-slate-500">{s.rationale}</p>
                        {s.horizonMonths && (
                          <p className="mt-1 text-xs text-slate-400">
                            Horizon: {s.horizonMonths} months
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-brand-500">
                          {formatMoney(s.amountMinor, DEFAULT_CURRENCY)}
                        </p>
                        <span
                          className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            RISK_COLOR[s.riskLevel] ?? "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {s.riskLevel} risk
                        </span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              <p className="px-2 text-xs text-slate-400">{suggest.data.disclaimer}</p>
            </>
          )}
        </>
      )}
    </div>
  );
}
